/**
 * Removes duplicate sessions and sorts them by start time.
 *
 * @param sessions - An array of {@link Transformed.Timeslot} objects, potentially containing duplicates.
 * @returns An array of unique {@link Transformed.Timeslot} objects, sorted by their start time.
 */
export const removeDuplicatesAndSort = (sessions: Transformed.Timeslot[]): Transformed.Timeslot[] => {
  const uniqueSessions = new Map<string, Transformed.Timeslot>()

  sessions.forEach((session) => {
    if (!uniqueSessions.has(session.id)) {
      uniqueSessions.set(session.id, session)
    }
  })

  // Sort to ensure correct order if eventTickets[0].timeslot was prepended
  return [...uniqueSessions.values()].sort((a, b) => (
    new Date(a.startTime).getTime() - new Date(b.startTime).getTime()))
}

/**
 * Determines which sessions to display based on the selected session index,
 * ensuring a focused view around the selected item.
 *
 * @param selectedIndex - The index of the currently selected session within the
 * `timeslots` array. A value of -1 indicates the `selectedTimeslot` is not found in `timeslots`.
 * @param timeslots - An array of {@link Transformed.Timeslot} objects representing all available sessions.
 * @param selectedTimeslot - The currently selected {@link Transformed.Timeslot} object. This is used when
 * `selectedIndex` is -1.
 * @returns An object containing:
 * - `startIndex`: The starting index of the displayed sessions within the original `timeslots` array.
 * - `endIndex`: The ending index (exclusive) of the displayed sessions within the original `timeslots` array.
 * - `sessions`: An array of unique {@link Transformed.Timeslot} objects to be displayed, sorted by their start time.
 */
export const getSessionsToDisplay = (
  selectedIndex: number,
  timeslots: Transformed.Timeslot[],
  selectedTimeslot: Transformed.Timeslot,
): { startIndex: number; endIndex: number; sessions: Transformed.Timeslot[] } => {
  let startIndex = 0
  let endIndex = 4
  let sessionsToDisplay: Transformed.Timeslot[] = []

  if (selectedIndex === -1) {
    // If the selected session is not found in eventDays,
    // it means it's outside the initial slice.
    // We'll show the selected session and the first 3 eventDays.
    sessionsToDisplay = [selectedTimeslot, ...timeslots.slice(0, 3)]
    startIndex = 0
    endIndex = 3
  } else if (selectedIndex <= 1) {
    // If selected is among the first two, show the first 4
    startIndex = 0
    endIndex = 4
    sessionsToDisplay = timeslots.slice(startIndex, endIndex)
  } else if (selectedIndex >= timeslots.length - 2) {
    // If selected is among the last two, show the last 4
    startIndex = Math.max(0, timeslots.length - 4)
    endIndex = timeslots.length
    sessionsToDisplay = timeslots.slice(startIndex, endIndex)
  } else {
    // Otherwise, show selected and 3 after (or center the selected)
    startIndex = selectedIndex
    endIndex = selectedIndex + 4
    sessionsToDisplay = timeslots.slice(startIndex, endIndex)
  }

  return {
    startIndex,
    endIndex,
    sessions: removeDuplicatesAndSort(sessionsToDisplay),
  }
}

/**
 * Determines if day tiles should render. based on isRecurringEvent, eventTickets, and eventDays.
 *
 * @param {boolean} isRecurringEvent - Whether the event is configured as a recurring event
 * @param {Transformed.EventTicketsAndAddonsWithTimeslots[]} eventTickets - Array of event tickets with associated
 * timeslot data
 * @param {Transformed.Timeslot[]} eventDays - Array of available event day timeslots
 * @returns {boolean} True if all conditions are met and day tiles should be rendered, false otherwise
 */
export const shouldRenderDayTiles = (
  isRecurringEvent: boolean,
  eventTickets: Transformed.EventTicketsAndAddonsWithTimeslots[],
  eventDays: Transformed.Timeslot[],
): boolean => Boolean(
  isRecurringEvent
  && eventTickets
  && eventTickets.length > 0
  && eventDays
  && eventDays.length > 0,
)
